import { Fragment } from 'react';
import intl from 'react-intl-universal';
import { htmlToText } from 'html-to-text';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Icon from '@mui/material/Icon';
import { CircularProgress, Tooltip } from '@mui/material';

import MDBadge from 'components/MDBadge';
import MDTypography from 'components/MDTypography';
import { formatBytes } from 'utils/misc';
import { renderSelectedTagsChips } from 'views/New/utils/utils';
import { MISSION, MISSION_STATUS } from 'shared/constants/uploadAttachments';
import { MISSION_CATEGORY } from 'context/constants';
import logger from 'utils/logger';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CancelIcon from '@mui/icons-material/Cancel';

const log = logger.createLogger('UploadMissions');

export const GENDER_OPTIONS = {
  FEMALE: 'F',
  MALE: 'M',
  OTHERS: 'X',
};

export const GENDER_TEXT = {
  [GENDER_OPTIONS.FEMALE]: 'new_option_patient_gender_female',
  [GENDER_OPTIONS.MALE]: 'new_option_patient_gender_male',
  [GENDER_OPTIONS.OTHERS]: 'new_option_patient_gender_others',
};

const EMERGENCY_VALUES = {
  NO_EMERGENCY: 1,
  IN_1_HOUR: 5,
  IN_3_HOURS: 4,
  IN_24_HOURS: 3,
  IN_THREE_DAYS: 6,
};

export const EMERGENCY_TEXT = {
  [EMERGENCY_VALUES.NO_EMERGENCY]: 'new_option_emergency_no_emergency',
  [EMERGENCY_VALUES.IN_1_HOUR]: 'new_option_emergency_in_1_hour',
  [EMERGENCY_VALUES.IN_3_HOURS]: 'new_option_emergency_in_3_hours',
  [EMERGENCY_VALUES.IN_24_HOURS]: 'new_option_emergency_in_24_hours',
  [EMERGENCY_VALUES.IN_THREE_DAYS]: 'new_option_emergency_in_3_days',
};

export const getBadge = (missionStatus, size) => {
  var color = 'info';
  var label;
  switch (missionStatus) {
    case MISSION_STATUS.INIT:
      label = intl.get('upload_mission_menu_item_status_init');
      break;
    case MISSION_STATUS.UPLOADING:
      label = intl.get('upload_mission_menu_item_status_uploading');
      break;
    case MISSION_STATUS.ABORTING_UPLOAD:
      label = intl.get('upload_mission_menu_item_status_aborting');
      color = 'warning';
      break;
    case MISSION_STATUS.UPLOAD_ABORTED:
      color = 'warning';
      label = intl.get('upload_mission_menu_item_status_aborted');
      break;
    case MISSION_STATUS.UPLOAD_FAILED:
      color = 'error';
      label = intl.get('upload_mission_menu_item_status_upload_failed');
      break;
    case MISSION_STATUS.PROCESSING:
      label = intl.get('upload_mission_menu_item_status_processing');
      break;
    case MISSION_STATUS.PROCESS_FAILED:
      color = 'error';
      label = intl.get('upload_mission_menu_item_status_process_failed');
      break;
    case MISSION_STATUS.PROCESS_COMPLETE:
      color = 'success';
      label = intl.get('upload_mission_menu_item_status_done');
      break;
    default:
      break;
  }
  return (
    <MDBadge size={size} color={color} badgeContent={label} container square />
  );
};

const createBasicInfo = (mission) => (
  <Fragment>
    <Grid size={12}>
      <Stack direction="row" spacing={1} alignItems="center">
        <MDTypography fontWeight="bold">{`${intl.get(
          'upload_mission_menu_item_type'
        )}: `}</MDTypography>
        <MDTypography>{getMissionTypeDisplayString(mission.type)}</MDTypography>
      </Stack>
    </Grid>
    <Grid size={12}>
      <Stack direction="row" spacing={1} alignItems="center">
        <MDTypography fontWeight="bold">
          {`${intl.get('upload_mission_menu_item_issue_title')}:`}
        </MDTypography>
        <MDTypography data-testid="upload-mission-modal-issueIdentifierId">{`${mission.issueIdentifierId}`}</MDTypography>
      </Stack>
    </Grid>
  </Fragment>
);

export const createFormData = (formState, isKenshin = false) => {
  let fullGenderName;
  let emergencyName;

  if (formState.patientGender) {
    fullGenderName = intl.get(GENDER_TEXT[formState.patientGender]);
  }
  if (formState.emergency) {
    emergencyName = intl.get(EMERGENCY_TEXT[formState.emergency]);
  }

  const formData = {
    new_label_send_to: {
      value: formState.dstOrgName,
      recommended: true,
      showKenshin: true,
    },
    new_label_emergency: {
      value: emergencyName,
      recommended: true,
      showKenshin: true,
    },
    new_label_patient_name: {
      value: formState.patientName,
      recommended: true,
      showKenshin: false,
    },
    new_label_patient_gender: {
      value: fullGenderName,
      recommended: false,
      showKenshin: false,
    },
    new_label_department: {
      value: formState.department,
      recommended: false,
      showKenshin: false,
    },
    new_label_doctor_in_charge: {
      value: formState.doctorsInCharge.join(', '),
      recommended: false,
      showKenshin: false,
    },
  };

  const formValues = [];
  for (const [key, value] of Object.entries(formData)) {
    if (isKenshin && !value.showKenshin) {
      continue;
    }
    formValues.push(
      <Grid size={6} key={key}>
        <Stack direction="column" spacing={1}>
          <MDTypography>
            <span
              style={{
                fontWeight: 'bold',
                color: value.recommended && !value.value ? 'red' : 'black',
              }}>
              {intl.get(key)}:{' '}
            </span>
            <span>{value.value}</span>
          </MDTypography>
          {value.recommended && !value.value && (
            <span style={{ color: 'red', fontSize: '0.7em' }}>
              {intl.get('new_label_recommended')}
            </span>
          )}
        </Stack>
      </Grid>
    );
  }
  formValues.push(
    <Grid size={12} key={'new_label_case_description_label'}>
      <MDTypography>
        <span style={{ fontWeight: 'bold' }}>
          {intl.get('new_label_case_description')}:
        </span>
      </MDTypography>
    </Grid>
  );
  formValues.push(
    <Grid xs={12} item key={'new_label_case_description_desc'}>
      <MDTypography variant="body2" mt={-2}>
        {htmlToText(formState.description)}
      </MDTypography>
    </Grid>
  );
  return formValues;
};

const getFilesList = (mission) => {
  const structuredAttachments = mission.structuredAttachments;
  const missionStatus = mission.status;
  const completedUploads = mission.completedUploads;
  const failedUploads = mission.failedUploads;
  return structuredAttachments.map((item, index) => {
    const isCompleted = completedUploads.some(
      (upload) => upload.name === item.name
    );

    const failedUpload = failedUploads.find(
      (upload) => upload.name === item.name
    );

    const isFailed = failedUpload ? true : false;

    return (
      <Grid size={12} key={index}>
        <Stack
          direction="row"
          spacing={4}
          justifyContent="space-between"
          alignItems="center">
          <Stack
            direction="row"
            spacing={4}
            alignItems="center"
            sx={{ flex: 1 }}>
            <Icon fontSize="large" color="secondary">
              {item.type === 'directory' ? 'folder' : 'attachment'}
            </Icon>
            <Stack direction="column" spacing={1}>
              <MDTypography variant="body2" style={{ marginTop: '10px' }}>
                {intl.get('new_label_file_name')}: {item.name}
              </MDTypography>
              <MDTypography variant="body2">
                {intl.get('new_label_file_size')}: {formatBytes(item.size)}{' '}
              </MDTypography>
            </Stack>
          </Stack>

          {missionStatus === MISSION_STATUS.UPLOAD_ABORTED ? (
            <Icon fontSize="large" color="error">
              <CancelIcon />
            </Icon>
          ) : isCompleted ? (
            <Icon fontSize="large" color="success">
              <CheckCircleIcon />
            </Icon>
          ) : isFailed ? (
            <Icon fontSize="large" color="error">
              <Tooltip title={failedUpload.error}>
                <ErrorIcon />
              </Tooltip>
            </Icon>
          ) : (
            <CircularProgress size={24} color="primary" />
          )}
        </Stack>
      </Grid>
    );
  });
};

export const generateMissionDetailsModalContents = (mission, darkMode) => {
  return (
    <Grid spacing={1} container style={{ marginTop: '5px', maxHeight: '80vh' }}>
      {createBasicInfo(mission)}
      {mission.type === MISSION.ISSUE &&
        createFormData(
          mission.formState,
          mission.missionCategory === MISSION_CATEGORY.KENSHIN
        )}
      {mission.type === MISSION.ISSUE &&
        renderSelectedTagsChips(
          mission.selectedTagIds,
          mission.tagList,
          darkMode
        )}
      {getFilesList(mission)}
    </Grid>
  );
};

export const getMissionTypeDisplayString = (missionType) => {
  let displayString;
  switch (missionType) {
    case MISSION.ISSUE:
      displayString = intl.get('upload_mission_type_new_issue');
      break;
    case MISSION.NEW_ATTACHMENTS_ON_EXISTING_ISSUE:
      displayString = intl.get(
        'upload_mission_type_new_attachments_on_existing_issue'
      );
      break;
    default:
      break;
  }

  return displayString;
};
